import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../contexts/LanguageContext'
import { PageContent } from '../components/Layout'
import { API_BASE_URL } from '../utils/api'

// TypeScript interfaces for calendar events
export interface CalendarEvent {
  id: string
  title: string
  start: string
  end?: string
  allDay?: boolean
  backgroundColor?: string
  borderColor?: string
  textColor?: string
  extendedProps?: {
    type: 'task' | 'deal' | 'lead' | 'appointment' | 'custom'
    description?: string
    priority?: 'low' | 'medium' | 'high' | 'urgent'
    status?: string
    entityId?: number
    entityType?: string
    customerName?: string
    customerEmail?: string
    customerPhone?: string
    meetingType?: string
    notes?: string
  }
}

type ViewMode = 'month' | 'week' | 'day'

const Calendar: React.FC = () => {
  const { t } = useTranslation()
  const { language } = useLanguage()

  // State management
  const [viewMode, setViewMode] = useState<ViewMode>('month')
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [dateEventsModal, setDateEventsModal] = useState<{ isOpen: boolean; date: Date | null; events: CalendarEvent[] }>({
    isOpen: false,
    date: null,
    events: []
  })
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [eventForm, setEventForm] = useState({
    title: '',
    start: '',
    end: '',
    allDay: false,
    type: 'custom' as 'task' | 'deal' | 'lead' | 'appointment' | 'custom',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent'
  })

  // Fetch calendar events from API
  const fetchEvents = async (startDate?: string, endDate?: string) => {
    try {
      setLoading(true)
      
      const start = startDate || new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).toISOString()
      const end = endDate || new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0, 23, 59, 59).toISOString()
      
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/calendar/calendar-data?start=${start}&end=${end}&include_tasks=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch events: ${response.status}`)
      }
      
      const data = await response.json()
      setEvents(data.events || [])
    } catch (error) {
      console.error('Error fetching calendar events:', error)
      setEvents([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [currentMonth])

  // Helper functions
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()
    
    return { daysInMonth, startingDayOfWeek, year, month }
  }

  const getEventsForDate = (date: Date): CalendarEvent[] => {
    const dateString = formatLocalDate(date)
    
    return events.filter(event => {
      if (!event.start) return false
      
      let eventDateString: string
      if (typeof event.start === 'string') {
        eventDateString = event.start.includes('T') ? event.start.split('T')[0] : event.start
      } else {
        eventDateString = formatLocalDate(new Date(event.start))
      }
      
      return eventDateString === dateString
    })
  }

  const changeMonth = (increment: number) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev)
      newDate.setMonth(newDate.getMonth() + increment)
      return newDate
    })
  }

  const goToToday = () => {
    setCurrentMonth(new Date())
  }

  const getWeekDays = (date: Date) => {
    const day = date.getDay()
    const diff = date.getDate() - day // Adjust to Sunday
    const sunday = new Date(date)
    sunday.setDate(diff)
    
    const days = []
    for (let i = 0; i < 7; i++) {
      const d = new Date(sunday)
      d.setDate(sunday.getDate() + i)
      days.push(d)
    }
    return days
  }

  const changeWeek = (increment: number) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev)
      newDate.setDate(newDate.getDate() + (increment * 7))
      return newDate
    })
  }

  const changeDay = (increment: number) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev)
      newDate.setDate(newDate.getDate() + increment)
      return newDate
    })
  }

  const getTimeSlots = () => {
    const slots = []
    for (let hour = 0; hour < 24; hour++) {
      slots.push({
        hour,
        label: `${String(hour).padStart(2, '0')}:00`
      })
    }
    return slots
  }

  const getEventsForDateTime = (date: Date, hour: number): CalendarEvent[] => {
    const dateString = formatLocalDate(date)
    
    return events.filter(event => {
      if (!event.start) return false
      
      let eventDateString: string
      let eventHour: number | null = null
      
      if (typeof event.start === 'string') {
        const parts = event.start.split('T')
        eventDateString = parts[0]
        if (parts[1]) {
          eventHour = parseInt(parts[1].split(':')[0])
        }
      } else {
        const eventDate = new Date(event.start)
        eventDateString = formatLocalDate(eventDate)
        eventHour = eventDate.getHours()
      }
      
      return eventDateString === dateString && (event.allDay || eventHour === hour)
    })
  }

  const getEventColor = (event: CalendarEvent) => {
    const type = event.extendedProps?.type || 'custom'
    const priority = event.extendedProps?.priority || 'medium'
    
    if (priority === 'urgent') return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-500'
    if (priority === 'high') return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-500'
    
    switch (type) {
      case 'task': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-500'
      case 'deal': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-500'
      case 'lead': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-500'
      case 'appointment': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-500'
      default: return 'bg-gray-100 dark:bg-gray-700/50 text-gray-800 dark:text-gray-300 border-gray-500'
    }
  }

  const handleCreateEvent = async () => {
    if (!eventForm.title.trim()) return

    try {
      const token = localStorage.getItem('token')
      
      const formatLocalDateTime = (dateTimeString: string): string => {
        if (!dateTimeString) return dateTimeString
        if (dateTimeString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/) && !dateTimeString.includes('Z')) {
          return dateTimeString
        }
        
        const date = new Date(dateTimeString)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        
        return `${year}-${month}-${day}T${hours}:${minutes}`
      }
      
      const backendData = {
        title: eventForm.title,
        description: eventForm.description || '',
        start_time: formatLocalDateTime(eventForm.start),
        end_time: formatLocalDateTime(eventForm.end || eventForm.start),
        all_day: eventForm.allDay || false,
        background_color: '#3B82F6',
        border_color: '#2563EB',
        text_color: '#FFFFFF',
        priority: eventForm.priority || 'medium',
        status: 'active',
        event_type: eventForm.type || 'custom'
      }

      const response = await fetch(`${API_BASE_URL}/api/calendar/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(backendData)
      })

      if (!response.ok) {
        throw new Error(`Failed to create event: ${response.status}`)
      }

      await fetchEvents()
      setShowCreateModal(false)
      resetForm()
    } catch (error) {
      console.error('Failed to create event:', error)
      alert('Failed to create event. Please try again.')
    }
  }

  const resetForm = () => {
    setEventForm({
      title: '',
      start: '',
      end: '',
      allDay: false,
      type: 'custom',
      description: '',
      priority: 'medium'
    })
  }

  return (
    <PageContent>
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('calendar.title')}
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              {t('calendar.subtitle')}
            </p>
          </div>
          
          {/* View Mode Buttons */}
          <div className="flex items-center space-x-2 w-full sm:w-auto">
            {['month', 'week', 'day'].map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode as ViewMode)}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === mode
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {t(`calendar.views.${mode}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'month' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Calendar Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => changeMonth(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {currentMonth.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <button
                onClick={goToToday}
                className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors font-medium"
              >
                {t('dashboard.periods.today')}
              </button>
            </div>
            
            <button
              onClick={() => changeMonth(1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Calendar and Side Panel Container */}
          <div className="flex flex-col lg:flex-row">
            {/* Calendar Grid */}
            <div className={`p-2 sm:p-4 transition-all duration-300 ${dateEventsModal.isOpen ? 'lg:w-[71.4%]' : 'w-full'}`}>
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                  <div key={day} className="text-center text-xs font-semibold text-gray-600 dark:text-gray-400 py-2">
                    <span className="hidden sm:inline">{language === 'fr' ? ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][idx] : day}</span>
                    <span className="inline sm:hidden">{(language === 'fr' ? ['D', 'L', 'M', 'M', 'J', 'V', 'S'][idx] : ['S', 'M', 'T', 'W', 'T', 'F', 'S'][idx])}</span>
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {(() => {
                  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth)
                  const days = []
                  const today = new Date()
                  const todayString = formatLocalDate(today)

                  // Empty cells for days before month starts
                  for (let i = 0; i < startingDayOfWeek; i++) {
                    days.push(
                      <div key={`empty-${i}`} className="aspect-square min-h-[100px] bg-gray-50 dark:bg-gray-900/50 rounded-lg" />
                    )
                  }

                  // Days of the month
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(year, month, day)
                    const dateString = formatLocalDate(date)
                    const isToday = dateString === todayString
                    const dayEvents = getEventsForDate(date)
                    const isSelected = dateEventsModal.isOpen && dateEventsModal.date && formatLocalDate(dateEventsModal.date) === dateString

                    days.push(
                      <div
                        key={day}
                        onClick={() => {
                          if (dayEvents.length > 0) {
                            setDateEventsModal({ isOpen: true, date, events: dayEvents })
                          }
                        }}
                        className={`aspect-square min-h-[60px] sm:min-h-[80px] lg:min-h-[100px] rounded-lg border-2 p-1 sm:p-2 transition-all ${
                          dayEvents.length > 0 ? 'cursor-pointer' : 'cursor-default'
                        } ${
                          isToday
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : isSelected
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className={`text-xs sm:text-sm font-semibold ${
                            isToday || isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'
                          }`}>
                            {day}
                          </div>
                          {dayEvents.length > 0 && (
                            <div className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-blue-500 text-white text-xs font-bold">
                              {dayEvents.length > 9 ? '9+' : dayEvents.length}
                            </div>
                          )}
                        </div>
                        <div className="space-y-1 overflow-y-auto max-h-[40px] sm:max-h-[60px] lg:max-h-[70px] widget-scrollbar">
                          {dayEvents.slice(0, 2).map(event => (
                            <div
                              key={event.id}
                              className={`text-xs px-1 sm:px-1.5 py-0.5 rounded truncate ${getEventColor(event)}`}
                            >
                              <span className="hidden sm:inline">{event.title}</span>
                              <span className="inline sm:hidden">•</span>
                            </div>
                          ))}
                          {dayEvents.length > 2 && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 px-1 sm:px-1.5 font-medium">
                              <span className="hidden sm:inline">+{dayEvents.length - 2} more...</span>
                              <span className="inline sm:hidden">+{dayEvents.length - 2}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  }

                  return days
                })()}
              </div>
            </div>

            {/* Side Panel for Selected Date Events */}
            {dateEventsModal.isOpen && dateEventsModal.date && (
              <div className="w-full lg:w-[28.6%] border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-4 overflow-y-auto max-h-[600px] lg:max-h-[800px] widget-scrollbar mt-4 lg:mt-0">
                {/* Side Panel Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                      {dateEventsModal.date.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { 
                        weekday: 'long',
                        day: 'numeric'
                      })}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                      {dateEventsModal.events.length} {dateEventsModal.events.length === 1 ? t('calendar.event') : t('calendar.events')}
                    </p>
                  </div>
                  <button
                    onClick={() => setDateEventsModal({ isOpen: false, date: null, events: [] })}
                    className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Events List */}
                <div className="space-y-3">
                  {dateEventsModal.events.map(event => (
                    <div
                      key={event.id}
                      className="bg-white dark:bg-gray-800 rounded-lg p-3 cursor-pointer hover:shadow-md dark:hover:bg-gray-750 transition-all border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2 flex-wrap gap-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getEventColor(event)}`}>
                            {event.extendedProps?.type?.toUpperCase() || 'EVENT'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="text-sm font-medium text-gray-900 dark:text-white mb-2 line-clamp-2">
                        {event.title}
                      </div>
                      
                      {event.extendedProps?.description && (
                        <div className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                          {event.extendedProps.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Week View */}
      {viewMode === 'week' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Week Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => changeWeek(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {(() => {
                  const weekDays = getWeekDays(currentMonth)
                  const startDate = weekDays[0]
                  const endDate = weekDays[6]
                  return `${startDate.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                })()}
              </h2>
              <button
                onClick={goToToday}
                className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors font-medium"
              >
                {t('dashboard.periods.today')}
              </button>
            </div>
            
            <button
              onClick={() => changeWeek(1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Week Grid */}
          <div className="p-2 sm:p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {getWeekDays(currentMonth).map((date, idx) => {
                const dateString = formatLocalDate(date)
                const isToday = dateString === formatLocalDate(new Date())
                const dayEvents = getEventsForDate(date)
                const isSelected = dateEventsModal.isOpen && dateEventsModal.date && formatLocalDate(dateEventsModal.date) === dateString

                return (
                  <div key={idx} className="min-h-[300px] sm:min-h-[350px] lg:min-h-[400px] border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div 
                      className={`text-center py-2 border-b border-gray-200 dark:border-gray-700 cursor-pointer ${
                        isToday
                          ? 'bg-blue-500 text-white'
                          : isSelected
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                          : 'bg-gray-50 dark:bg-gray-900/50 text-gray-900 dark:text-white'
                      }`}
                      onClick={() => {
                        if (dayEvents.length > 0) {
                          setDateEventsModal({ isOpen: true, date, events: dayEvents })
                        }
                      }}
                    >
                      <div className="text-xs font-semibold">
                        {date.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { weekday: 'short' })}
                      </div>
                      <div className="text-lg font-bold">{date.getDate()}</div>
                      {dayEvents.length > 0 && (
                        <div className="text-xs">{dayEvents.length} events</div>
                      )}
                    </div>
                    <div className="p-2 space-y-1 overflow-y-auto max-h-[250px] sm:max-h-[300px] lg:max-h-[350px] widget-scrollbar">
                      {dayEvents.map(event => (
                        <div
                          key={event.id}
                          className={`text-xs px-2 py-1.5 rounded cursor-pointer hover:shadow-md transition-shadow ${getEventColor(event)}`}
                        >
                          <div className="font-semibold truncate">{event.title}</div>
                          {event.start && !event.allDay && (
                            <div className="text-xs opacity-75">
                              {new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Day View */}
      {viewMode === 'day' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Day Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => changeDay(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {currentMonth.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { 
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </h2>
              <button
                onClick={goToToday}
                className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors font-medium"
              >
                {t('dashboard.periods.today')}
              </button>
            </div>
            
            <button
              onClick={() => changeDay(1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Day Timeline */}
          <div className="p-2 sm:p-4 overflow-y-auto max-h-[600px] sm:max-h-[700px] lg:max-h-[800px] widget-scrollbar">
            <div className="space-y-1">
              {getTimeSlots().map(slot => {
                const slotEvents = getEventsForDateTime(currentMonth, slot.hour)
                
                return (
                  <div key={slot.hour} className="flex border-t border-gray-200 dark:border-gray-700">
                    <div className="w-12 sm:w-16 lg:w-20 py-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">
                      {slot.label}
                    </div>
                    <div className="flex-1 min-h-[50px] sm:min-h-[60px] p-1 sm:p-2 space-y-1">
                      {slotEvents.map(event => (
                        <div
                          key={event.id}
                          className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded cursor-pointer hover:shadow-md transition-shadow ${getEventColor(event)}`}
                        >
                          <div className="text-sm font-semibold truncate">{event.title}</div>
                          {event.extendedProps?.description && (
                            <div className="text-xs mt-1 opacity-75 line-clamp-2 hidden sm:block">
                              {event.extendedProps.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Create Event Button */}
      <button
        onClick={() => {
          const now = new Date()
          now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15, 0, 0)
          
          const year = now.getFullYear()
          const month = String(now.getMonth() + 1).padStart(2, '0')
          const day = String(now.getDate()).padStart(2, '0')
          const hours = String(now.getHours()).padStart(2, '0')
          const minutes = String(now.getMinutes()).padStart(2, '0')
          
          const startTime = `${year}-${month}-${day}T${hours}:${minutes}`
          const endDate = new Date(now.getTime() + 60 * 60000)
          const endHours = String(endDate.getHours()).padStart(2, '0')
          const endMinutes = String(endDate.getMinutes()).padStart(2, '0')
          const endTime = `${year}-${month}-${day}T${endHours}:${endMinutes}`
          
          setEventForm({
            ...eventForm,
            start: startTime,
            end: endTime,
            allDay: false
          })
          setShowCreateModal(true)
        }}
        className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 p-3 sm:p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all hover:shadow-xl z-40"
        aria-label="Create Event"
      >
        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      </button>

      {/* Create Event Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto widget-scrollbar">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('calendar.createModal.title')}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  resetForm()
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('calendar.createModal.fields.eventTitle')} *
                </label>
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('calendar.createModal.fields.eventTitlePlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('calendar.createModal.fields.eventType')}
                </label>
                <select
                  value={eventForm.type}
                  onChange={(e) => setEventForm({...eventForm, type: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="custom">{t('calendar.createModal.types.custom')}</option>
                  <option value="task">{t('calendar.createModal.types.task')}</option>
                  <option value="deal">{t('calendar.createModal.types.deal')}</option>
                  <option value="lead">{t('calendar.createModal.types.lead')}</option>
                  <option value="appointment">{t('calendar.createModal.types.appointment')}</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('calendar.createModal.fields.startDate')}
                  </label>
                  <input
                    type="date"
                    value={eventForm.start.split('T')[0] || ''}
                    onChange={(e) => {
                      const time = eventForm.start.split('T')[1] || '09:00'
                      setEventForm({...eventForm, start: `${e.target.value}T${time}`})
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-2 mb-1">
                    {t('calendar.createModal.fields.startTime')}
                  </label>
                  <input
                    type="time"
                    value={eventForm.start.split('T')[1] || ''}
                    onChange={(e) => {
                      const date = eventForm.start.split('T')[0] || new Date().toISOString().split('T')[0]
                      setEventForm({...eventForm, start: `${date}T${e.target.value}`})
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="900"
                    disabled={eventForm.allDay}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('calendar.createModal.fields.endDate')}
                  </label>
                  <input
                    type="date"
                    value={eventForm.end.split('T')[0] || ''}
                    onChange={(e) => {
                      const time = eventForm.end.split('T')[1] || '10:00'
                      setEventForm({...eventForm, end: `${e.target.value}T${time}`})
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-2 mb-1">
                    {t('calendar.createModal.fields.endTime')}
                  </label>
                  <input
                    type="time"
                    value={eventForm.end.split('T')[1] || ''}
                    onChange={(e) => {
                      const date = eventForm.end.split('T')[0] || new Date().toISOString().split('T')[0]
                      setEventForm({...eventForm, end: `${date}T${e.target.value}`})
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="900"
                    disabled={eventForm.allDay}
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="allDay"
                  checked={eventForm.allDay}
                  onChange={(e) => setEventForm({...eventForm, allDay: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <label htmlFor="allDay" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  {t('calendar.createModal.fields.allDayEvent')}
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('calendar.createModal.fields.priority')}
                </label>
                <select
                  value={eventForm.priority}
                  onChange={(e) => setEventForm({...eventForm, priority: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">{t('calendar.createModal.priorities.low')}</option>
                  <option value="medium">{t('calendar.createModal.priorities.medium')}</option>
                  <option value="high">{t('calendar.createModal.priorities.high')}</option>
                  <option value="urgent">{t('calendar.createModal.priorities.urgent')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('calendar.createModal.fields.description')}
                </label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('calendar.createModal.fields.descriptionPlaceholder')}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  resetForm()
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('calendar.createModal.actions.cancel')}
              </button>
              <button
                onClick={handleCreateEvent}
                disabled={!eventForm.title.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('calendar.createModal.actions.createEvent')}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContent>
  )
}

export default Calendar
